import SvgIcon from '@home/components/SvgIcon';
import { NO_DATE } from '@op/constants';
import ChannelLabel from '@op/components/ChannelLabel';
import {
  decimalFormatPercentage,
  percentageFormat,
  reviewsFormat,
} from '@op/utils/value';
import { getShowCount } from '@op/utils';
import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import CardCommon from '@op/components/CardCommon';
import { RankKeyMeticsItem } from '../../component/TrendCardTips';
import { useGetRankList } from '../../hooks/rank';
import moment from 'moment';

type SteamItemType = {
  review: number;
  score: number;
  name?: string;
};
interface IReviewsProgressProps {
  title: string;
  rankTitle?: string;
  data: SteamItemType;
  filter: any;
  games: any;
  gameData: any;
  by_type: string;
  showRank?: boolean;
  rankLogId?: string;
}
export default function ReviewsProgress({
  title,
  rankTitle,
  data,
  filter,
  games,
  gameData,
  by_type,
  showRank = true,
  rankLogId,
}: IReviewsProgressProps) {
  const params = useMemo(
    () => ({
      ...filter,
      games,
      stats_types: ['game_store'],
      tab_type: 'steam',
      by_type,
    }),
    [by_type, filter, games],
  );

  const {
    data: rankData,
    loading: rankLoading,
    getRankList,
  } = useGetRankList({
    filter: params,
    gameData,
  });

  const { formatMessage } = useIntl();

  const { review, score, goodRatio, badRatio, text } = useMemo(() => {
    const { review, score, name } = data || {};
    return {
      review,
      score,
      goodRatio: score * 100,
      badRatio: review === 0 ? 0 : 100 - score * 100,
      text: name,
    };
  }, [data]);

  const { emptyDescText, isEmpty, hasEmptyTitle } = useMemo(() => {
    let emptyDescText = '';
    let hasEmptyTitle = true;

    const isEmpty = !review && !score;
    const { release_time: release } =
      gameData?.[filter?.edition_unified_id] || {};
    const formatDate = (date: moment.MomentInput) => moment(date, 'YYYY-MM-DD');

    if (isEmpty) {
      if (['recent_reviews', 'recent_reviews_positive'].includes(by_type)) {
        const valid =
          release &&
          formatDate(filter?.end_time).diff(formatDate(release), 'days') > 30;
        if (!valid) {
          emptyDescText = 'Data available on the 31st day after launch';
          hasEmptyTitle = false;
        }
      }
      if (['all_reviews', 'all_reviews_positive'].includes(by_type)) {
        const valid =
          release &&
          formatDate(filter?.end_time).isAfter(formatDate(release), 'days');
        if (!valid) {
          emptyDescText = 'Data available after launch';
          hasEmptyTitle = false;
        }
      }
    }

    return {
      emptyDescText,
      isEmpty,
      hasEmptyTitle,
    };
  }, [by_type, filter, gameData, review, score]);

  // 计算好评值\差评值 使用舆情格式化方法
  const goodReviews = Math.ceil(review * score);
  const negativeReviews = review - goodReviews;
  const goodReviewsValue = isNaN(goodReviews) ? '-' : getShowCount(goodReviews);
  const negativeReviewsValue = isNaN(negativeReviews)
    ? '-'
    : getShowCount(negativeReviews);

  return (
    <CardCommon
      className="positive-mixed"
      contentCard
      empty={isEmpty}
      emptyHeight={125}
      hasEmptyImg={false}
      emptyDescText={emptyDescText}
      hasEmptyTitle={hasEmptyTitle}
      loading={rankLoading}
      loadingHeight={125}
      title={
        <div className="positive-mixed-title">
          <ChannelLabel channel_name="steam" showText={false}></ChannelLabel>
          {/* <SvgIcon type="steam-icon" className="positive-mixed-icon" /> */}
          <span className="positive-mixed-title-text">
            {formatMessage({ id: title })}
          </span>
        </div>
      }
    >
      <div className="positive-mixed-desc">
        {formatMessage({ id: text || NO_DATE.EMPTY })}
      </div>
      <div className="positive-mixed-num">
        {reviewsFormat(review)} {formatMessage({ id: 'Reviews-1' })}
      </div>
      <div className="positive-mixed-box">
        <div className="positive-mixed-box-item">
          <SvgIcon type="good-icon" className="positive-mixed-box-icon" />
          <span className="positive-mixed-box-item-text">
            {formatMessage({ id: 'Positive' })}
          </span>
          <div className="positive-mixed-box-progress">
            <div
              className="positive-mixed-box-progress-inner"
              style={{ width: `${goodRatio}%` }}
            ></div>
          </div>
          <div className="positive-mixed-box-progress-data">{`${goodReviewsValue}(${percentageFormat(
            goodRatio,
            '-',
            0,
          )})`}</div>
        </div>
        <div className="positive-mixed-box-item positive-mixed-box-item-nogood">
          <SvgIcon type="no-good-icon" className="positive-mixed-box-icon" />
          <span className="positive-mixed-box-item-text">
            {formatMessage({ id: 'Negative' })}
          </span>
          <div className="positive-mixed-box-progress">
            <div
              className="positive-mixed-box-progress-inner"
              style={{ width: `${badRatio}%` }}
            ></div>
          </div>
          <div className="positive-mixed-box-progress-data">{`${negativeReviewsValue}(${percentageFormat(
            badRatio,
            '-',
            0,
          )})`}</div>
        </div>
      </div>
      <RankKeyMeticsItem
        getRankList={getRankList}
        valueKey={'score'}
        noShow={showRank}
        title={rankTitle || title}
        logId={rankLogId}
        valueFormat={(val) => decimalFormatPercentage(val, '-', 0)}
      ></RankKeyMeticsItem>
    </CardCommon>
  );
}
